<script setup>
import SavedFilterLoadError from 'dashboard/components-next/filter/SavedFilterLoadError.vue';
import {
  onBeforeUnmount,
  onMounted,
  computed,
  ref,
  reactive,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { usePolicy } from 'dashboard/composables/usePolicy';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import ContactsListLayout from 'dashboard/components-next/Contacts/ContactsListLayout.vue';
import ContactEmptyState from 'dashboard/components-next/Contacts/EmptyState/ContactEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsList from 'dashboard/components-next/Contacts/Pages/ContactsList.vue';
import ContactBulkOperations from '../components/ContactBulkOperations.vue';
import ContactImportHistory from '../components/ContactImportHistory.vue';
import ContactExportHistory from '../components/ContactExportHistory.vue';
import ContactsBulkActionBar from '../components/ContactsBulkActionBar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';
import ContactAPI from 'dashboard/api/contacts';
import { submitContactBulkRequest } from 'dashboard/helper/contactBulkRequest';

const DEFAULT_SORT_FIELD = 'last_activity_at';
// SiteUp: quem nunca escolheu uma ordem vê primeiro quem falou por último,
// como na lista de conversas. A escolha salva continua valendo.
const DEFAULT_SORT_ORDER = '-';
const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();
const { isWorkspaceV2 } = useWorkspaceV2();
const { checkPermissions } = usePolicy();
const canImportContacts = computed(() =>
  checkPermissions(['administrator', 'contact_manage'])
);

const contacts = useMapGetter('contacts/getContactsList');
const uiFlags = useMapGetter('contacts/getUIFlags');
const customViewsUiFlags = useMapGetter('customViews/getUIFlags');
const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const meta = useMapGetter('contacts/getMeta');

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const contactEmptyStateRef = ref(null);
const pageNumber = computed(() => Number(route.query?.page) || 1);
// For infinite scroll in search, track page internally
const searchPageNumber = ref(1);
const isLoadingMore = ref(false);
const searchError = ref(false);
const filterError = ref(null);
const listError = ref(null);
const isSearchingFirstPage = ref(false);
let searchRevision = 0;
onBeforeUnmount(() => {
  searchRevision += 1;
});

const parseSortSettings = (sortString = '') => {
  if (!sortString) {
    return { sort: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_ORDER };
  }
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { contacts_sort_by: contactSortBy = '' } = uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(contactSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeLabel = computed(() => route.params.label);
const activeSegmentId = computed(() => route.params.segmentId);
const isFetchingList = computed(
  () => uiFlags.value.isFetching || customViewsUiFlags.value.isFetching
);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);
const hasMore = computed(() => meta.value?.hasMore ?? false);
const isSearchView = computed(() => !!searchQuery.value);

const selectedContactIds = ref([]);
const isBulkActionLoading = ref(false);
const bulkDeleteDialogRef = ref(null);
const importHistoryRef = ref(null);
const revealImportHistory = () => {
  if (isWorkspaceV2.value && canImportContacts.value) {
    importHistoryRef.value?.reveal();
  }
};
// Exportar pede a mesma permissão de importar (administrador).
const exportHistoryRef = ref(null);
const revealExportHistory = () => {
  if (isWorkspaceV2.value && canImportContacts.value) {
    exportHistoryRef.value?.reveal();
  }
};
const selectedCount = computed(() => selectedContactIds.value.length);
const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);
const hasSelection = computed(() => selectedCount.value > 0);
const activeSegment = computed(() => {
  if (!activeSegmentId.value) return undefined;
  return segments.value.find(view => view.id === Number(activeSegmentId.value));
});
// SiteUp: segmento apagado ou link antigo. Sem isso a tela mantinha a lista
// da visita anterior sob um título vazio, com ações em massa sobre ela.
const isSegmentMissing = computed(
  () =>
    Boolean(activeSegmentId.value) &&
    !activeSegment.value &&
    !customViewsUiFlags.value.isFetchingContact &&
    !customViewsUiFlags.value.hasContactError
);

const hasContacts = computed(() => contacts.value.length > 0);
const isContactIndexView = computed(
  () => route.name === 'contacts_dashboard_index' && pageNumber.value === 1
);
const isActiveView = computed(() => route.name === 'contacts_dashboard_active');
const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length > 0;
});

const showEmptyStateLayout = computed(() => {
  return (
    !searchQuery.value &&
    !hasContacts.value &&
    isContactIndexView.value &&
    !hasAppliedFilters.value
  );
});
const showEmptyText = computed(() => {
  return (
    (searchQuery.value ||
      hasAppliedFilters.value ||
      !isContactIndexView.value) &&
    !hasContacts.value
  );
});

const headerTitle = computed(() => {
  if (searchQuery.value) return t('CONTACTS_LAYOUT.HEADER.SEARCH_TITLE');
  if (isActiveView.value) return t('CONTACTS_LAYOUT.HEADER.ACTIVE_TITLE');
  if (isSegmentMissing.value)
    return t('CONTACTS_LAYOUT.SEGMENT_NOT_FOUND.TITLE');
  if (activeSegmentId.value) return activeSegment.value?.name;
  if (activeLabel.value) return `#${activeLabel.value}`;
  return t('CONTACTS_LAYOUT.HEADER.TITLE');
});

const emptyStateMessage = computed(() => {
  if (isActiveView.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.ACTIVE_EMPTY_STATE_TITLE');
  if (!searchQuery.value || hasAppliedFilters.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  return t('CONTACTS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

const visibleContactIds = computed(() =>
  contacts.value.map(contact => contact.id)
);

const clearSelection = () => {
  selectedContactIds.value = [];
};

const openBulkDeleteDialog = () => {
  if (!selectedContactIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const toggleSelectAll = shouldSelect => {
  const currentSelection = new Set(selectedContactIds.value);
  if (shouldSelect) {
    visibleContactIds.value.forEach(id => currentSelection.add(id));
  } else {
    visibleContactIds.value.forEach(id => currentSelection.delete(id));
  }
  selectedContactIds.value = Array.from(currentSelection);
};

const toggleContactSelection = ({ id, value }) => {
  const isAlreadySelected = selectedContactIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedContactIds.value = [...selectedContactIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedContactIds.value = selectedContactIds.value.filter(
      contactId => contactId !== id
    );
  }
};

const updatePageParam = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...(search ? { search } : {}),
  };

  if (!search) {
    delete query.search;
  }

  router.replace({ query });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
  label: activeLabel.value,
});

const fetchContacts = async (page = 1, options = {}) => {
  searchRevision += 1;
  const revision = searchRevision;
  const isCurrent = () => revision === searchRevision;
  isSearchingFirstPage.value = false;
  isLoadingMore.value = false;
  searchError.value = false;
  filterError.value = null;
  listError.value = null;
  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }
  await store.dispatch('contacts/clearContactFilters');
  if (!isCurrent()) return;
  try {
    await store.dispatch('contacts/get', {
      ...getCommonFetchParams(page),
      isCurrent,
      throwError: true,
    });
    if (isCurrent()) updatePageParam(page);
  } catch {
    if (isCurrent()) listError.value = { page };
  }
};

const fetchSavedOrAppliedFilteredContact = async (
  payload,
  page = 1,
  options = {}
) => {
  if (!activeSegmentId.value && !hasAppliedFilters.value) return;
  searchRevision += 1;
  const revision = searchRevision;
  const isCurrent = () => revision === searchRevision;
  isSearchingFirstPage.value = false;
  isLoadingMore.value = false;
  searchError.value = false;
  filterError.value = null;
  listError.value = null;

  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }

  try {
    await store.dispatch('contacts/filter', {
      ...getCommonFetchParams(page),
      queryPayload: payload,
      isCurrent,
      throwError: true,
    });
    if (isCurrent()) updatePageParam(page);
  } catch (error) {
    if (isCurrent()) filterError.value = { payload, page };
  }
};

const fetchActiveContacts = async (page = 1, options = {}) => {
  searchRevision += 1;
  const revision = searchRevision;
  const isCurrent = () => revision === searchRevision;
  isSearchingFirstPage.value = false;
  isLoadingMore.value = false;
  searchError.value = false;
  filterError.value = null;
  listError.value = null;
  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }

  await store.dispatch('contacts/clearContactFilters');
  if (!isCurrent()) return;
  try {
    await store.dispatch('contacts/active', {
      page,
      sortAttr: buildSortAttr(),
      isCurrent,
      throwError: true,
    });
    if (isCurrent()) updatePageParam(page);
  } catch {
    if (isCurrent()) listError.value = { page };
  }
};

const performSearch = debounce(
  async (revision, value, page = 1, append = false, options = {}) => {
    const isCurrent = () => revision === searchRevision;
    if (!isCurrent()) return;
    const { clearSelection: shouldClearSelection = true } = options;
    searchError.value = false;
    filterError.value = null;
    listError.value = null;

    if (!append) {
      searchPageNumber.value = 1;

      if (shouldClearSelection) {
        clearSelection();
      }
    }
    await store.dispatch('contacts/clearContactFilters');
    if (!isCurrent()) return;
    searchValue.value = value;

    if (!value) {
      updatePageParam(page);
      await fetchContacts(page, { clearSelection: false });
      return;
    }

    updatePageParam(page, value);
    isSearchingFirstPage.value = true;
    try {
      await store.dispatch('contacts/search', {
        ...getCommonFetchParams(page),
        search: value,
        append,
        throwError: true,
        isCurrent,
      });
      if (isCurrent()) searchPageNumber.value = page;
    } catch {
      if (isCurrent()) searchError.value = true;
    } finally {
      if (isCurrent()) isSearchingFirstPage.value = false;
    }
  },
  DEBOUNCE_DELAY
);

const searchContacts = (value, page = 1, append = false, options = {}) => {
  searchRevision += 1;
  isLoadingMore.value = false;
  return performSearch(searchRevision, value, page, append, options);
};

const loadMoreSearchResults = async () => {
  if (
    !hasMore.value ||
    isLoadingMore.value ||
    searchError.value ||
    isSearchingFirstPage.value
  )
    return;

  const revision = searchRevision;
  const isCurrent = () => revision === searchRevision;
  isLoadingMore.value = true;
  const nextPage = searchPageNumber.value + 1;

  try {
    await store.dispatch('contacts/search', {
      ...getCommonFetchParams(nextPage),
      search: searchValue.value,
      append: true,
      throwError: true,
      isCurrent,
    });
    if (isCurrent()) searchPageNumber.value = nextPage;
  } catch {
    if (isCurrent()) useAlert(t('CONTACTS_LAYOUT.LOAD_MORE_ERROR'));
  } finally {
    if (isCurrent()) isLoadingMore.value = false;
  }
};

const fetchContactsBasedOnContext = async (page, options = {}) => {
  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }
  updatePageParam(page, searchValue.value);
  if (searchQuery.value) {
    await searchContacts(searchQuery.value, page, false, {
      clearSelection: shouldClearSelection,
    });
    return;
  }
  // Reset the search value when we change the view
  searchValue.value = '';
  // If we're on the active route, fetch active contacts
  if (isActiveView.value) {
    await fetchActiveContacts(page, {
      clearSelection: shouldClearSelection,
    });
    return;
  }
  // If there are applied filters or active segment with query
  if (
    (hasAppliedFilters.value || activeSegment.value?.query) &&
    !activeLabel.value
  ) {
    const queryPayload =
      activeSegment.value?.query || filterQueryGenerator(appliedFilters.value);
    await fetchSavedOrAppliedFilteredContact(queryPayload, page, {
      clearSelection: shouldClearSelection,
    });
    return;
  }
  // Default case: fetch regular contacts + label
  await fetchContacts(page, {
    clearSelection: shouldClearSelection,
  });
};

// Ação em massa pode esvaziar a página atual (apagar, ou tirar a etiqueta que
// define a lista): volta uma página em vez de mostrar "nenhum contato" sem
// rodapé para voltar.
const refetchAfterBulkAction = async () => {
  const page = pageNumber.value;
  await fetchContactsBasedOnContext(page);
  if (page > 1 && !listError.value && !contacts.value?.length) {
    await fetchContactsBasedOnContext(page - 1);
  }
};

const onPageChange = page =>
  fetchContactsBasedOnContext(page, { clearSelection: false });

const bulkHistoryRef = ref(null);
const submitBulkAction = async payload => {
  if (!isWorkspaceV2.value) return BulkActionsAPI.create(payload);
  const accountId = route.params.accountId;
  const userId = store.getters.getCurrentUserID;
  const response = await submitContactBulkRequest({
    accountId,
    userId,
    isCurrent: () =>
      route.params.accountId === accountId &&
      store.getters.getCurrentUserID === userId,
    payload,
  });
  bulkHistoryRef.value?.reveal();
  return response;
};

const assignLabels = async labels => {
  if (
    isBulkActionLoading.value ||
    !labels.length ||
    !selectedContactIds.value.length
  ) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await submitBulkAction({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { add: labels },
    });
    if (!isWorkspaceV2.value)
      useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await refetchAfterBulkAction();
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const removeLabels = async labels => {
  if (
    isBulkActionLoading.value ||
    !labels.length ||
    !selectedContactIds.value.length
  ) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await submitBulkAction({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { remove: labels },
    });
    if (!isWorkspaceV2.value)
      useAlert(t('CONTACTS_BULK_ACTIONS.REMOVE_LABELS_SUCCESS'));
    clearSelection();
    await refetchAfterBulkAction();
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.REMOVE_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteContacts = async () => {
  if (isBulkActionLoading.value || !selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await submitBulkAction({
      type: 'Contact',
      ids: selectedContactIds.value,
      action_name: 'delete',
    });
    if (!isWorkspaceV2.value)
      useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await refetchAfterBulkAction();
    bulkDeleteDialogRef.value?.close?.();
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    contacts_sort_by: buildSortAttr(),
  });

  if (searchQuery.value) {
    await searchContacts(searchValue.value, pageNumber.value, false, {
      clearSelection: false,
    });
    return;
  }

  if (isActiveView.value) {
    await fetchActiveContacts();
    return;
  }

  await (activeSegmentId.value || hasAppliedFilters.value
    ? fetchSavedOrAppliedFilteredContact(
        activeSegmentId.value
          ? activeSegment.value?.query
          : filterQueryGenerator(appliedFilters.value)
      )
    : fetchContacts());
};

const createContact = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    contactEmptyStateRef.value?.onSuccess();
  } catch {
    contactEmptyStateRef.value?.onError(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
    );
  }
};

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
  }
});

watch(
  () => uiSettings.value?.contacts_sort_by,
  newSortBy => {
    if (newSortBy) {
      const { sort, order } = parseSortSettings(newSortBy);
      sortState.activeSort = sort;
      sortState.activeOrdering = order;
    }
  },
  { immediate: true }
);

watch(
  [activeLabel, activeSegment, isActiveView],
  () => {
    fetchContactsBasedOnContext(pageNumber.value);
  },
  { deep: true }
);

watch(isSegmentMissing, (missing, wasMissing) => {
  if (missing) {
    clearSelection();
    return;
  }
  // Voltar para Todos os contatos não muda label, segmento nem ativos, então
  // o watch acima não dispara e a lista antiga ficaria na tela.
  if (wasMissing && route.name === 'contacts_dashboard_index') fetchContacts();
});

// SiteUp: aviso discreto quando há duplicatas do WhatsApp sem decisão da
// equipe. Só as contagens (summary), uma vez por conta aberta.
const pendingWahaDuplicates = ref(0);
let wahaSummaryRequest = 0;
const fetchWahaDuplicatesSummary = async () => {
  wahaSummaryRequest += 1;
  const request = wahaSummaryRequest;
  pendingWahaDuplicates.value = 0;
  try {
    const { data } = await ContactAPI.getWahaDuplicatesSummary();
    if (request === wahaSummaryRequest)
      pendingWahaDuplicates.value = Number(data?.meta?.pending_count) || 0;
  } catch {
    // Aviso opcional: sem ele a lista funciona igual.
  }
};
watch(() => route.params.accountId, fetchWahaDuplicatesSummary, {
  immediate: true,
});
const showWahaDuplicatesNotice = computed(
  () => pendingWahaDuplicates.value > 0 && !isSegmentMissing.value
);
const goToWahaDuplicates = () =>
  router.push({
    name: 'contacts_waha_duplicates',
    params: { accountId: route.params.accountId },
  });

const goToAllContacts = () =>
  router.push({
    name: 'contacts_dashboard_index',
    params: { accountId: route.params.accountId },
  });

watch(searchQuery, value => {
  if (isFetchingList.value) return;
  searchValue.value = value || '';
  // Reset the view if there is search query when we click on the sidebar group
  if (value === undefined) {
    if (
      isActiveView.value ||
      activeLabel.value ||
      activeSegment.value ||
      hasAppliedFilters.value
    )
      return;
    fetchContacts();
  }
});

// Segmento apagado em outra aba ou link antigo: recarrega a lista de
// segmentos (pode ter sido criado em outra aba). Se continuar ausente, a tela
// mostra o aviso "segmento não encontrado" com o botão de voltar.
watch(
  () => [route.params.accountId, activeSegmentId.value],
  () => {
    if (!activeSegmentId.value || activeSegment.value) return;
    store.dispatch('customViews/get', 'contact');
  },
  { immediate: true }
);

onMounted(async () => {
  if (!activeSegmentId.value) {
    if (searchQuery.value) {
      await searchContacts(searchQuery.value, pageNumber.value, false, {
        clearSelection: false,
      });
      return;
    }
    if (isActiveView.value) {
      await fetchActiveContacts(pageNumber.value);
      return;
    }
    await fetchContacts(pageNumber.value);
  } else if (activeSegment.value && activeSegmentId.value) {
    await fetchSavedOrAppliedFilteredContact(
      activeSegment.value.query,
      pageNumber.value
    );
  }
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
    :class="{
      '!bg-n-background text-[15px] [&_h1]:text-[26px] [&_h2]:text-xl':
        isWorkspaceV2,
    }"
    data-testid="page-contacts"
  >
    <div v-if="isWorkspaceV2" class="shrink-0 px-6">
      <div class="mx-auto w-full max-w-5xl">
        <ContactBulkOperations
          :key="route.params.accountId"
          ref="bulkHistoryRef"
          class="!mx-0"
          @result-updated="
            fetchContactsBasedOnContext(pageNumber, { clearSelection: false })
          "
        />
        <ContactImportHistory
          v-if="canImportContacts"
          :key="`imports-${route.params.accountId}`"
          ref="importHistoryRef"
          class="!mx-0"
          @result-updated="
            fetchContactsBasedOnContext(pageNumber, { clearSelection: false })
          "
        />
        <ContactExportHistory
          v-if="canImportContacts"
          :key="`exports-${route.params.accountId}`"
          ref="exportHistoryRef"
          class="!mx-0"
        />
      </div>
    </div>
    <SavedFilterLoadError filter-type="contact" />
    <ContactsListLayout
      :key="route.params.accountId"
      :search-value="searchValue"
      :header-title="headerTitle"
      :current-page="currentPage"
      :total-items="totalItems"
      :show-pagination-footer="!isFetchingList && hasContacts && !isSearchView"
      :active-sort="sortState.activeSort"
      :active-ordering="sortState.activeOrdering"
      :active-segment="activeSegment"
      :segments-id="activeSegmentId"
      :is-fetching-list="isFetchingList"
      :has-applied-filters="hasAppliedFilters"
      :use-infinite-scroll="isSearchView"
      :has-more="!searchError && !isSearchingFirstPage && hasMore"
      :is-loading-more="isLoadingMore"
      @update:current-page="onPageChange"
      @search="
        value => searchContacts(value, 1, false, { clearSelection: false })
      "
      @update:sort="handleSort"
      @apply-filter="fetchSavedOrAppliedFilteredContact"
      @clear-filters="fetchContacts"
      @load-more="loadMoreSearchResults"
      @import-accepted="revealImportHistory"
      @export-accepted="revealExportHistory"
    >
      <div
        v-if="showWahaDuplicatesNotice"
        class="mx-4 mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
        data-testid="waha-duplicates-notice"
      >
        <span class="flex min-w-0 items-center gap-2">
          <span class="i-ri-whatsapp-line size-4 shrink-0 text-n-slate-11" />
          {{
            t(
              'CONTACTS_LAYOUT.WAHA_DUPLICATES.LIST_NOTICE.TEXT',
              { count: pendingWahaDuplicates },
              pendingWahaDuplicates
            )
          }}
        </span>
        <Button
          :label="t('CONTACTS_LAYOUT.WAHA_DUPLICATES.LIST_NOTICE.ACTION')"
          color="slate"
          variant="faded"
          size="sm"
          class="!min-h-11 !rounded-xl"
          @click="goToWahaDuplicates"
        />
      </div>
      <div
        v-if="isSegmentMissing"
        role="alert"
        class="mx-4 my-6 flex flex-col items-start gap-3 rounded-xl border border-n-weak p-4 text-n-slate-12"
      >
        <p class="m-0">
          {{ t('CONTACTS_LAYOUT.SEGMENT_NOT_FOUND.DESCRIPTION') }}
        </p>
        <Button
          :label="t('CONTACTS_LAYOUT.SEGMENT_NOT_FOUND.BACK')"
          class="!min-h-11 !rounded-xl"
          @click="goToAllContacts"
        />
      </div>
      <div
        v-else-if="listError"
        role="alert"
        class="mx-4 my-6 flex flex-col items-start gap-3 rounded-xl border border-n-weak p-4 text-n-slate-12"
      >
        <p class="m-0">{{ t('CONTACTS_LAYOUT.LIST_ERROR') }}</p>
        <Button
          :label="t('CONTACTS_LAYOUT.RETRY_SEARCH')"
          class="!min-h-11 !rounded-xl"
          @click="fetchContactsBasedOnContext(listError.page)"
        />
      </div>
      <div
        v-else-if="filterError"
        role="alert"
        class="mx-4 my-6 flex flex-col items-start gap-3 rounded-xl border border-n-weak p-4 text-n-slate-12"
      >
        <p class="m-0">{{ t('CONTACTS_LAYOUT.FILTER_ERROR') }}</p>
        <Button
          :label="t('CONTACTS_LAYOUT.RETRY_SEARCH')"
          class="!min-h-11 !rounded-xl"
          @click="
            fetchSavedOrAppliedFilteredContact(
              filterError.payload,
              filterError.page,
              { clearSelection: false }
            )
          "
        />
      </div>
      <div
        v-else-if="searchError && !isSearchingFirstPage"
        role="alert"
        class="mx-4 my-6 flex flex-col items-start gap-3 rounded-xl border border-n-weak p-4 text-n-slate-12"
      >
        <p class="m-0">{{ t('CONTACTS_LAYOUT.SEARCH_ERROR') }}</p>
        <Button
          :label="t('CONTACTS_LAYOUT.RETRY_SEARCH')"
          class="!min-h-11 !rounded-xl"
          @click="
            searchContacts(searchValue, 1, false, { clearSelection: false })
          "
        />
      </div>
      <div
        v-else-if="
          isSearchingFirstPage ||
          (isFetchingList && !(isSearchView && hasContacts))
        "
        class="flex items-center justify-center py-10 text-n-slate-11"
        :class="{ '!min-h-48': isWorkspaceV2 }"
      >
        <Spinner />
      </div>

      <template v-else>
        <ContactsBulkActionBar
          v-if="hasSelection"
          :visible-contact-ids="visibleContactIds"
          :selected-contact-ids="selectedContactIds"
          :is-loading="isBulkActionLoading"
          @toggle-all="toggleSelectAll"
          @clear-selection="clearSelection"
          @assign-labels="assignLabels"
          @remove-labels="removeLabels"
          @delete-selected="openBulkDeleteDialog"
        />
        <ContactEmptyState
          v-if="showEmptyStateLayout"
          ref="contactEmptyStateRef"
          class="pt-14"
          :title="t('CONTACTS_LAYOUT.EMPTY_STATE.TITLE')"
          :subtitle="t('CONTACTS_LAYOUT.EMPTY_STATE.SUBTITLE')"
          :button-label="t('CONTACTS_LAYOUT.EMPTY_STATE.BUTTON_LABEL')"
          @create="createContact"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
          :class="{
            '!mx-6 !my-8 !min-h-48 !rounded-2xl !border !border-dashed !border-n-weak !px-6':
              isWorkspaceV2,
          }"
        >
          <span
            class="text-base text-n-slate-11"
            :class="{ '!text-[15px]': isWorkspaceV2 }"
          >
            {{ emptyStateMessage }}
          </span>
        </div>

        <div
          v-else
          class="flex flex-col gap-4 pt-4 pb-6"
          :class="{
            '!gap-3 !pt-3 [&_table]:text-[15px] [&_thead]:text-[13px] [&_tbody_tr]:border-b [&_tbody_tr]:border-n-weak [&_tbody_tr]:transition-colors [&_tbody_tr]:duration-150 [&_tbody_tr:hover]:bg-n-alpha-1':
              isWorkspaceV2,
          }"
        >
          <ContactsList
            :contacts="contacts"
            :selected-contact-ids="selectedContactIds"
            @toggle-contact="toggleContactSelection"
          />
          <Dialog
            v-if="selectedCount"
            ref="bulkDeleteDialogRef"
            type="alert"
            :title="bulkDeleteDialogTitle"
            :description="bulkDeleteDialogDescription"
            :confirm-button-label="bulkDeleteDialogConfirmLabel"
            :is-loading="isBulkActionLoading"
            @confirm="deleteContacts"
          />
        </div>
      </template>
    </ContactsListLayout>
  </div>
</template>
